import React from 'react';
import { Link } from 'react-router-dom';
import FeaturedImage from './FeaturedImage';
import { generateProductUrl } from '../utils/urlEncoding';
import './FeaturedProductCard.css';

// Base64 encoded placeholder image
const PLACEHOLDER_IMAGE = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjIwIiBoZWlnaHQ9IjIyMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjIwIiBoZWlnaHQ9IjIyMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjExMCIgeT0iMTIwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTVweCIgZmlsbD0iIzk5OTk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSI+Tm8gSW1hZ2U8L3RleHQ+PC9zdmc+';

// Helper function for alt text
const getProductAlt = (product) => {
  return `${product.name} - ${product.shortDescription || 'Product from OriginSelect'}`;
};

// Format price with CAD and fixed decimals
const formatPrice = (price) => {
  return `CAD $${Number(price).toFixed(2)}`;
};

const FeaturedProductCard = ({ product, countryCode, handleProductClick, index }) => {
  // Determine if we should display the price (hide if -1, 0, undefined, or null)
  const shouldDisplayPrice = product.price !== -1 && product.price !== 0 && product.price !== undefined && product.price !== null && product.price > 0;
  
  // Check if this is a skeleton placeholder
  const isSkeletonPlaceholder = product.isSkeletonPlaceholder || !product.imageUrl;
  
  return (
    <div 
      className={`featured-product-card ${isSkeletonPlaceholder ? 'skeleton-placeholder' : ''}`}
      onClick={() => !isSkeletonPlaceholder && handleProductClick(product)}
      tabIndex={isSkeletonPlaceholder ? -1 : 0}
      role={isSkeletonPlaceholder ? "presentation" : "button"}
      aria-label={isSkeletonPlaceholder ? "Loading product..." : `View details for ${product.name}`}
      onKeyDown={(e) => {
        if (!isSkeletonPlaceholder && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          handleProductClick(product);
        }
      }}
    >
      <div className="featured-product-image-container">
        {isSkeletonPlaceholder ? (
          <div className="skeleton-image-placeholder">
            <div className="skeleton-shimmer"></div>
          </div>
        ) : (
          <FeaturedImage
            src={product.imageUrl || product.imagePath || PLACEHOLDER_IMAGE}
            alt={getProductAlt(product)}
            className="featured-product-img"
            width="300"
            height="300"
            priority={true}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = PLACEHOLDER_IMAGE;
            }}
          />
        )}
      </div>
      
      <div className="featured-product-info">
        {isSkeletonPlaceholder ? (
          <>
            <div className="skeleton-text skeleton-title"></div>
            <div className="skeleton-text skeleton-price"></div>
            <div className="skeleton-button"></div>
          </>
        ) : (
          <>
            <h3 className="featured-product-name">{product.name}</h3>
            
            {shouldDisplayPrice && (
              <p className="featured-product-price">
                {formatPrice(product.price)}
              </p>
            )}
            
            {/* Two-button layout: Buy and Details */}
            <div className="featured-product-buttons-container">
              {(() => {
                // Check if any purchase links are available
                const hasAmazonLink = product.affiliateLinks?.some(link => 
                  link.name?.toLowerCase().includes('amazon')
                );
                const hasAffiliateLinks = product.affiliateLinks?.length > 0;
                const hasStoreLink = product.storeLink;
                const hasPurchaseLink = hasAmazonLink || hasAffiliateLinks || hasStoreLink;
                
                return (
                  <button
                    className="featured-buy-btn"
                    style={{
                      backgroundColor: hasPurchaseLink ? 'var(--primary-color, #E32D2D)' : '#cccccc',
                      opacity: hasPurchaseLink ? 1 : 0.6,
                    }}
                    disabled={!hasPurchaseLink}
                    onClick={(e) => {
                      e.stopPropagation();
                      
                      if (!hasPurchaseLink) return;
                      
                      // Check for Amazon link first, then any affiliate link, then store link
                      const amazonLink = product.affiliateLinks?.find(link => 
                        link.name?.toLowerCase().includes('amazon')
                      );
                      
                      let targetLink, linkType, linkName;
                      
                      if (amazonLink) {
                        targetLink = amazonLink.url;
                        linkType = 'amazon';
                        linkName = amazonLink.name || 'Amazon';
                      } else if (product.affiliateLinks?.length > 0) {
                        targetLink = product.affiliateLinks[0].url;
                        linkType = 'affiliate';
                        linkName = product.affiliateLinks[0].name || 'Affiliate';
                      } else if (product.storeLink) {
                        targetLink = product.storeLink;
                        linkType = 'store';
                        linkName = 'Store';
                      }
                      
                      // Track in Google Analytics
                      if (window.gtag) {
                        window.gtag('event', 'click', {
                          event_category: 'Ecommerce',
                          event_label: `Buy on ${linkName}: ${product.name}`,
                          event_action: 'featured_product_buy_click',
                          item_id: product._id,
                          item_name: product.name,
                          item_category: product.category || 'Featured',
                          item_variant: linkName,
                          price: product.price || 0,
                          affiliate_platform: linkType === 'amazon' ? 'Amazon' : linkName,
                          currency: countryCode === 'CA' ? 'CAD' : 'USD',
                          source: 'featured_products'
                        });
                      }
                      
                      // Open the link
                      if (targetLink) {
                        window.open(targetLink, '_blank', 'noopener,noreferrer');
                      }
                    }}
                    onMouseEnter={(e) => {
                      if (hasPurchaseLink) {
                        e.target.style.backgroundColor = '#c41e3a';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (hasPurchaseLink) {
                        e.target.style.backgroundColor = 'var(--primary-color, #E32D2D)';
                      }
                    }}
                  >
                    Buy
                  </button>
                );
              })()}
              
              <Link
                to={generateProductUrl(countryCode, product._id, product.name)}
                className="featured-details-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  
                  // Track in Google Analytics
                  if (window.gtag) {
                    window.gtag('event', 'click', {
                      event_category: 'Product Interaction',
                      event_label: `View Details: ${product.name}`,
                      event_action: 'featured_product_details_click',
                      item_id: product._id,
                      item_name: product.name,
                      item_category: product.category || 'Featured',
                      source: 'featured_products'
                    });
                  }
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--primary-color, #E32D2D)';
                  e.target.style.color = 'white';
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 8px rgba(227, 45, 45, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'white';
                  e.target.style.color = 'var(--primary-color, #E32D2D)';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 4px rgba(227, 45, 45, 0.1)';
                }}
              >
                Details
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FeaturedProductCard;